/* tslint:disable:no-inferrable-types no-trailing-whitespace import-spacing typedef-whitespace align whitespace */

import {DocumentWindow} from './document.window';
import {FileBrowserMetadata} from './file.browser.metadata';
import {DocumentWindowManager} from './document.window.manager';
import {Logger} from '@/util/logger';
import {UnDoRedo} from '@/util/un.do.redo';
import {basename, bytesToSize, dirname, errorAsText, isset} from '@/util/util';

import {BlobService} from '@/services/blob.service';
import {AuthenticationService} from '@/services/authentication.service';
import {IdeService} from '@/services/ide.service';
import {PropertiesHolder} from '@/models/properties.holder';
import {JSONBrowserMetadata} from './json.browser.metadata';
import {TextEditorMetadata} from './text.editor.metadata';
import {ModalService} from '@/services/modal.service';
import {ScenarioBrowserMetadata} from './scenario.browser.metadata';

import {FBNavCommand} from './f.b.nav.command';

import {TaskUpdateFileUpload} from '@/models/task.update.file.upload';
import {TaskUpdateFileUploadComplete} from '@/models/task.update.file.upload.complete';
import {TaskUpdateFileUploadFailed} from '@/models/task.update.file.upload.failed';

import {Blob as FileBlob, BlobType} from '../../models/blob';

import * as $ from 'jquery';

import 'ag-grid-community';
import 'ag-grid-enterprise';

import {catchError, first, tap} from 'rxjs/operators';
import {forkJoin, Observable, of} from 'rxjs';
import {ICommand} from "@/util/i.command";
import { HttpEvent, HttpEventType } from "@angular/common/http";
import {TaskProgressService} from "@/services/task.progress.service";
import {TaskUpdate} from "@/models/task.update";
import {TaskUpdateFileFolderDeleteFailed} from "@/models/task.update.file.folder.delete.failed";
import {TaskUpdateFileFolderDeleteComplete} from "@/models/task.update.file.folder.delete.complete";
import {TaskUpdateFileFolderDelete} from "@/models/task.update.file.folder.delete";

import {
  createGrid,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  ICellRendererParams,
  IRowNode
} from "ag-grid-community";

import {DateTime} from "luxon";
import {FileBrowserDatasource} from "@/screens/ide/file.browser.datasource";

/**
 *
 * @class FileBrowserWindow
 *
 * @brief window for general blob manager browsing, is intended be used in the tabbed center view of the GUI,
 * and you can have as many browser tabs as you like.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      April 2020
 *
 */
export class FileBrowserWindow extends DocumentWindow {

  /** access to the virtual file system */
  private blobber:           BlobService = null;

  /** access to user authentication */
  private auth:    AuthenticationService = null;

  /** access to user authentication */
  private ide:                IdeService = null;

  /** access to dialogs */
  private modals:           ModalService = null;

  /** the document manager we are a tab  of */
  private manager: DocumentWindowManager = null;

  /** the long-running tasks monitor in the GUI */
  private taskMonitor: TaskProgressService = null;

  /** the current focus of the file browser */
  private focus:                  string = '';

  /** show this element within focus (presumed to be a folder) */
  private highlight:              string = '';

  /** are we showing hidden files? */
  private showHidden:            boolean = false;

  /** the API for the widget */
  private api: GridApi                   = null;

  /** path to the user's project folder */
  private readonly projectPath: string            = '';

  /** path to the user's home folder */
  private readonly homePath: string               = '';

  /** path to the Quintiq incoming folder */
  private readonly quintiqPath: string            = '';

  /** timer to wait for user to stop typing */
  private typingTimer = null;

  /** the history manager for the diversion editor */
  public undoManager:           UnDoRedo = new UnDoRedo(`FileBrowserHistory`);

  /** standard constructor */
  constructor(
           meta: FileBrowserMetadata,
        manager: DocumentWindowManager
  ) {

    super(manager, meta, manager.layoutManager());

    Logger.debug('[FileBrowserWindow] constructing...');

    this.manager     = manager;
    this.auth        = this.manager.authService();
    this.blobber     = this.manager.blobService();
    this.ide         = this.manager.ideService();
    this.modals      = this.manager.modalsService();
    this.taskMonitor = this.manager.taskProgressService();
    this.focus       = meta.focus;
    this.highlight   = meta.highlight;
    this.homePath    = '/users/' +  this.auth.userOfSession.username;
    this.projectPath = this.homePath + '/projects';
    this.quintiqPath = '/incoming/quintiq';

    if (this.focus === '/') {

      this.focus = this.homePath;
    }

    /*
     * if they gave us a focus and highlight, and they are the same thing, they are saying
     * view that elements folders and highlight that element in its folder.
     *
     */

    if (this.focus == this.highlight) {

      this.highlight = basename(this.highlight);
      this.focus     = dirname(this.focus);

    }

    this.showBrowser();
  }

  /** restore the search view (on current 'focus') */
  public showBrowser() {

    Logger.debug(`[FileBrowserWindow] => ${this.focus} ...`);

    const container: JQuery<HTMLElement> = $(`#${this.meta.id}`);

    if (!$(container) || !$(container).is('div')) {

      this.unReady();

      Logger.error(`[FileBrowserWindow] missing hosting container (${this.meta.id})!`);

      return ;
    }

    $(container).html(`
<div class="gui-file-browser-wrapper">

<div class="add-blob-dialog-quickaction">
<label
  id="browse-blob-back-click-${this.meta.id}"
  class="gui-button"
  title="Previous Search">
  <i class="fas fa-arrow-left"></i>
</label>
<label
  id="browse-blob-forward-click-${this.meta.id}"
  class="gui-button"
  title="Next Search">
  <i class="fas fa-arrow-right" ></i>
</label>
<label
  id="browse-blob-up-click-${this.meta.id}"
  class="gui-button"
  title="Navigate to parent folder">
  <i class="fas fa-arrow-up"></i>
</label>
<label
  id="browse-blob-home-click-${this.meta.id}"
  class="gui-button"
  title="Home: ${this.homePath}">
  <i class="fas fa-home" ></i>
</label>
<label
  id="browse-blob-projects-click-${this.meta.id}"
  class="gui-button"
  title="Projects: ${this.projectPath}">
  <i class="fas fa-project-diagram" ></i>
</label>

<div class="browse-blob-quintiq-dropdown gui-button">
  <label
    id="browse-blob-quintiq-click-${this.meta.id}"
    class="browse-blob-quintiq-button"
    title="Ab-Ovo/Qunitiq: ${this.quintiqPath}">Q
  </label>
  <div
    id="browse-blob-quintiq-drop-button-wrapper-${this.meta.id}"
    class="browse-blob-quintiq-drop-button-wrapper">

    <label
      id="browse-blob-quintiq-drop-button-${this.meta.id}"
      class="browse-blob-quintiq-drop-button">
        <span class="chevron bottom"></span>
    </label>

    <div
      id="browse-blob-quintiq-drop-panel-${this.meta.id}"
      class="browse-blob-quintiq-drop-panel">

      <ul class="browse-blob-quintiq-drop-panel-content">
        <li folder="/incoming/quintiq/All scenarios" title="/incoming/quintiq/All scenarios">Scenarios</li>
        <li folder="/incoming/quintiq/All batches"   title="/incoming/quintiq/All batches">Batches</li>
        <li folder="/incoming/quintiq/dropbox/fav"   title="/incoming/quintiq/dropbox/fav">FAV Dropbox</li>
        <li folder="/incoming/quintiq/forecasts"     title="/incoming/quintiq/forecasts">Forecasts</li>
      </ul>

    </div>

  </div>

</div>

<label
  id="browse-blob-hidden-click-${this.meta.id}"
  class="gui-button"
  title="Show hidden files">
  <i class="far fa-eye-slash" ></i>
</label>
<label
  id="browse-blob-refresh-click-${this.meta.id}"
  class="gui-button"
  title="Refresh display">
  <i class="fas fa-sync-alt"></i>
</label>

</div>

<div class="browse-blob-dialog-path" style="margin-top: 12px;">
<input
  id="blob-path-input-${this.meta.id}"
  class="gui-input browse-blob-path-input"
  style="width: 100%;"
  type="text"
  title="Enter a path to focus on"
  placeholder="File or Folder Path">
</div>

<div class="gui-error-line"></div>

<div id="grid_table_${this.meta.id}" class="browse-ag-grid-wrap ag-theme-balham-dark">

</div>


</div>`);

    const checkExist = setInterval(() => {

      const dom: HTMLElement = document.getElementById(`grid_table_${this.meta.id}`) as HTMLElement;

      if (dom) {

        clearInterval(checkExist);

        /* setup quick actions */

        $(`#browse-blob-back-click-${this.meta.id}`).on('click', () => {

          if (this.undoManager.undoSize() === 0) {

            return ;
          }

          this.undoManager.undo();
        });

        $(`#browse-blob-forward-click-${this.meta.id}`).on('click', () => {

          if (this.undoManager.redoSize() === 0) {

            return ;
          }

          this.undoManager.redo();
        });

        $(`#browse-blob-up-click-${this.meta.id}`).on('click', () => {

          /* figure out parent folder */

          let parent: string = this.focus
            .replace(/\\/g, '/')
            .replace(/\/[^/]*\/?$/, '');

          if(parent === '') {

            parent = '/';
          }


          /* go! */

          this.navigate(parent);

        });

        $(`#browse-blob-home-click-${this.meta.id}`).on('click', () => {

          /* go! */

          this.navigate(this.homePath);

        });

        $(`#browse-blob-projects-click-${this.meta.id}`).on('click', () => {

          /* go! */

          this.navigate(this.projectPath);

        });

        $(`#browse-blob-quintiq-click-${this.meta.id}`).on('click', () => {

          /* go! */

          this.navigate(this.quintiqPath);

        });

        $(`#browse-blob-quintiq-drop-button-${this.meta.id}`).on('click', () => {

          /* go! */

          this.toggleQuintiqDropdown();

        });

        $(`#browse-blob-quintiq-drop-panel-${this.meta.id}`).find('li').each((idx: number, el: HTMLElement): void => {

          $(el).on('click', (): void => {

            let folder: string = $(el).attr('folder');

            this.goFolder(folder);

          });

        });

        $(`#browse-blob-hidden-click-${this.meta.id}`).on('click', () => {

          /* go! */

          this.showHidden = !this.showHidden;

          this.navigate(this.focus);

        });

        $(`#browse-blob-refresh-click-${this.meta.id}`).on('click', () => {

          /* go! */

          this.navigate(this.focus);

        });

        // noinspection JSJQueryEfficiency
        $(`#blob-path-input-${this.meta.id}`).on('input', (e) => {

          /* typing wait: @link https://stackoverflow.com/questions/10124114/wait-for-function-till-user-stops-typing */

          if (this.typingTimer) {

            clearTimeout(this.typingTimer);
          }

          this.typingTimer = setTimeout(() => {

            const path: string = $(e.currentTarget).val() as string;

            this.navigate(path);

          }, 400);

        });

        // noinspection JSJQueryEfficiency
        $(`#blob-path-input-${this.meta.id}`).on('keypress', (e) => {

          if (this.typingTimer) {

            clearTimeout(this.typingTimer);
          }

          this.typingTimer = setTimeout(() => {

            const path: string = ($(e.currentTarget).val() as string).trim();

            this.navigate(path);

          }, 400);

        });

        /*
         * for dropping stuff on Ag-Grid; you have to define your own handlers, Ag-Grid
         * doesn't have any built in support for the dropping part.
         *
         * @link: https://www.ag-grid.com/javascript-grid-drag-and-drop/
         *
         */

        const dropArea: HTMLElement = $(`#grid_table_${this.meta.id}`).get(0);

        dropArea.addEventListener('dragover', (event) => {

          event.preventDefault();
          event.stopPropagation();

          const dragSupported = event.dataTransfer.types.length;

          if (dragSupported) {

            event.dataTransfer.dropEffect = 'copy';

          }

        });

        dropArea.addEventListener('drop', (event) => {

          event.preventDefault();

          const isIE: boolean = window.navigator.userAgent.indexOf('Trident/') >= 0;

          /*
           * in theory we can handle dropping off custom JSON data from other parts of
           * our web application, for now, just look for incoming files from the user's
           * desktop, implied upload.
           *
           */

          const jsonData: string = event.dataTransfer.getData(isIE ? 'text' : 'application/json');
          const files:    File[] = Array.from(event.dataTransfer.files);


          if(files.length === 0) {

            /* nothing to do */

          }

          Logger.debug('[FileBrowserWindow] dropping files: ', files);

          /*
           * when uploading, we have no idea what the type of the files are...but we may
           * be able to guess from file extension.
           *
           */

          const typeMap: Map<string, string> = new Map<string, string>();

          for (const f of files) {

            const gt: BlobType =  FileBlob.guessType(BlobType.File, f.name, '');

            typeMap.set(f.name, FileBlob.stringOfType(gt));

          }

          this.doUpload(BlobType.File, files, typeMap);

        });

        /* setup Ag-Grid (table browser) */

        const columnDefs = [
          {
            dndSource: true,
            width: 32,
            dndSourceOnRowDrag: (params: any): void => {

              const row = params.rowNode;
              const e   = params.dragEvent;

              /* make sure this row is now the current selection */

              row.setSelected(true, true);

              /* fetch the current selection into transfer data */

              const transferRows: any = [];

              this.api.getSelectedRows().forEach((node) => {

                transferRows.push({
                  key:   node.key,
                  name:  node.name,
                  type:  node.type,
                  icon:  node.icon,
                  date:  node.date,
                  size:  node.size,
                  owner: node.owner
                });

              });

              /* encode as JSON */

              const jsonData          = JSON.stringify(transferRows);

              /* publish the data */

              if (window.navigator.userAgent.indexOf('Trident/') >= 0) {

                /* Internet Explorer is 'special' */

                e.dataTransfer.setData('text', jsonData);

              } else {

                e.dataTransfer.setData('application/json', jsonData);
                e.dataTransfer.setData('text/plain', jsonData);
              }

            }
          },
          {headerName: 'Name', field: 'name'},
          {
            headerName: 'Date',
            field:      'date',
            valueFormatter: (params: any): string => {

              /* use Luxon to display the date/time nicely */

              if (typeof params.value != 'string') {

                return '#####';
              }

              if (params.value == '') {

                return '';
              }

              return DateTime.fromISO(params.value.replace(' ', 'T')).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
            },

            comparator: (a: any, b: any) => {

              if (!a || a === '') {
                return -1;
              }

              if (!b || b === '') {

                return 1;
              }

              // @ts-ignore
              const ap: number[] = a.split(/[- :]/).map((x: any) => x as number);

              // @ts-ignore
              const bp: number[] = b.split(/[- :]/).map((x: any) => x as number);

              const aDate: Date = new Date(Date.UTC(ap[0], ap[1]-1, ap[2], ap[3], ap[4], ap[5]));
              const bDate: Date = new Date(Date.UTC(bp[0], bp[1]-1, bp[2], bp[3], bp[4], bp[5]));

              if (aDate > bDate) {
                return 1;
              }

              if (bDate > aDate) {

                return -1;
              }

              return 0;
            }
          },
          {
            headerName:   'Size',
            field:        'size',
            cellDataType: 'number',
            cellRenderer: (params: ICellRendererParams): string => {

              /* show size has human-readable byte sizing */

              return bytesToSize(params.value);
            }

          },
          {headerName: 'Type',       field: 'type'},
          {headerName: 'Instances',  field: 'instances'},
          {headerName: 'Files',      field: 'files'},
          {headerName: 'Full Path',  field: 'key'},
        ];

        const gridOptions: GridOptions = {
          columnDefs,
          cacheBlockSize: 100,
          rowModelType: 'infinite',
          datasource: null,
          rowDragManaged: true,
          defaultColDef: {
            sortable:              true,
            resizable:             true,
            cellRenderer:  (params: any) => {

              if (params.colDef.field == 'date') {

                if (typeof params.value != 'string') {

                  return '#####';
                }

                if (params.value == '') {

                  return '';
                }

                return DateTime.fromISO(params.value.replace(' ', 'T')).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
              }

              if (params.colDef.field !== 'name') {

                return params.value;
              }

              if (!params?.data) {

                return '';
              }

              return params.data.icon + params.data.name;

            }
          },
          rowSelection:                   'multiple',
          enableCellTextSelection:        true,
          suppressRowDeselection:         true,
          animateRows:                    true,
          enableRangeSelection:           true,
          allowContextMenuWithControlKey: true,
          popupParent:                    document.querySelector('body'),
          onRowDoubleClicked: (row) => {

            const path: string = row.data.key;

            if(row.data.type !== 'folder') {

              return ;
            }

            this.navigate(path);
          },

          onRowClicked: (row) => {

            const ph   = new PropertiesHolder();
            const data = row.data;

            ph.icon  = data.icon;
            ph.title = data.name;

            ph.set('Name', data.name);
            ph.set('Path', data.key);

            if (data.type !== 'folder') {

              ph.set('Date',      data.date);
              ph.set('Size',      data.size);
              ph.set('Type',      data.type);
              ph.set('Instances', data.instances);
              ph.set('Owner',     data.owner);
            }

            this.ide.focus(ph);
          },

          onCellContextMenu: (row) => {

            const node = row.node;

            if (!node.isSelected()) {

              this.api.deselectAll();

              node.setSelected(true, true);
            }

          },

          onGridReady: (data) => {

            this.api = data.api;

            Logger.debug(`[FileBrowserWindow] first navigation focus=${this.focus}, highlight=${this.highlight}`);

            this.navigate(this.focus);

          },

          getContextMenuItems: (params: GetContextMenuItemsParams) => {

            if (params.node) {

              if (!params.node.isSelected()) {

                this.api.deselectAll();

                params.node.setSelected(true, true);
              }
            }

            if (this.api.getSelectedRows().length == 0) {

              /* when nothing is selected to just show high level actions */

              return [
                {
                  name: 'New Folder',
                  action: () => {
                    this.newFolder();
                  },
                  icon: '<i class="fas fa-folder-plus"></i>'
                },
                'separator',

                {
                  name: 'New (Empty)',
                  action: () => {
                    this.newEmptyFile();
                  },
                  icon: '<i class="far fa-file"></i>'
                },
                {
                  name: 'Add (Upload)',
                  action: () => {

                    this.uploadFiles();
                  },
                  icon: '<i class="fas  fa-file-medical"></i>'
                }
              ];
            }

            let menu: any[] = [];

            if (!params.node || params.node.data.type === 'folder') {

              return [
                {
                  name: 'New Folder',
                  action: () => {
                    this.newFolder();
                  },
                  icon: '<i class="fas fa-folder-plus"></i>'
                },
                'separator',

                {
                  name: 'New (Empty)',
                  action: () => {
                    this.newEmptyFile();
                  },
                  icon: '<i class="far fa-file"></i>'
                },
                {
                  name: 'Add (Upload)',
                  action: () => {

                    this.uploadFiles();
                  },
                  icon: '<i class="fas  fa-file-medical"></i>'
                },
                'separator',
                {
                  name: 'Delete History',
                  action: () => {

                    this.deleteHistoryFilesFolders(this.api.getSelectedRows());
                  },
                  icon: '<i style="color: red;" class="fas fa-trash-alt"></i>'
                },
                'separator',
                'copy',
                'export'
              ];
            }

            /* file related actions */

            menu = menu.concat([

              {
                name: 'Browse Scenario',
                action: () => {

                  this.ide.launchWindow(new ScenarioBrowserMetadata(
                    '',
                    '',
                    params.node.data.key
                  ));
                },
                icon: '<i class="fas fa-table"></i>'
              },

              /*
              {
                name: 'View String Lines',
                action: () => {

                  this.ide.launchWindow(new StringLineViewerMetadata(
                    '',
                    '',
                    params.node.data.key,
                    '<i class="fas fa-train"></i>'
                  ));
                },
                icon: '<i class="fas fa-train"></i>'
              },
              */

              {
                name: 'JSON Edit',
                action: () => {

                  this.ide.launchWindow(new JSONBrowserMetadata(
                    '',
                    '',
                    params.node.data.key,
                    'json',
                    basename(params.node.data.key),
                    '<i class="fas fa-edit"></i>'
                  ));
                },
                icon: '<i class="fas fa-edit"></i>'
              },

              {
                name: 'Raw Edit',
                action: () => {

                  this.ide.launchWindow(new TextEditorMetadata(
                    '',
                    '',
                    params.node.data.key
                  ));

                },
                icon: '<i class="fas fa-keyboard"></i>'
              },
              {
                name: 'Download',
                action: () => {

                  this.downloadFile(params.node);
                },
                // @ts-ignore
                disabled: params.node.type === 'folder',
                icon: '<i class="fas fa-file-download"></i>'
              },
              {
                name: 'History',
                action: () => {

                  this.showHistory(params.node);
                },
                // @ts-ignore
                disabled: params.node.type === 'folder',
                icon: '<i class="fa fa-history"></i>'
              }
            ]);

            /* only show Delete Latest if it's a single selection, and not a folder */

            if (this.api.getSelectedRows().length < 2) {

              menu = menu.concat([
                {
                  name: 'Delete Latest',
                  action: () => {

                    this.deleteFilesFolders(this.api.getSelectedRows());
                  },
                  icon: '<i class="fas fa-trash-alt"></i>'
                }
              ]);

            }

            /*
             * if they select a single folder, or multiple items, it's a group, and
             * for groups of things, we only allow the brutal full delete option.
             * we can't do interactive pick and choose snapshots over an entire group.
             *
             */

            menu = menu.concat([
              {
                name: 'Delete History',
                action: () => {

                  this.deleteHistoryFilesFolders(this.api.getSelectedRows());
                },
                icon: '<i style="color: red;" class="fas fa-trash-alt"></i>'
              }
            ]);

            /* common actions... */

            menu = menu.concat([
              'separator',
              {
                name: 'New Folder',
                action: () => {
                  this.newFolder();
                },
                icon: '<i class="fas fa-folder-plus"></i>'
              },

              'separator',

              {
                name: 'New (Empty)',
                action: () => {
                  this.newEmptyFile();
                },
                icon: '<i class="far fa-file"></i>'
              },
              {
                name: 'Add (Upload)',
                action: () => {

                  this.uploadFiles();
                },
                icon: '<i class="fas  fa-file-medical"></i>'
              },
              'separator',
              'copy',
              'export'
            ]);

            return menu;
          },

        };

        this.api = createGrid(dom, gridOptions);

      }

    }, 100);
  }

  private showHistory(node: IRowNode) {

    if (node.data.type === 'folder') {

      Logger.error(`[FileBrowserWindow] tried to show history on folder: `, node);

      return  ;
    }

    const  key:   string = node.data.key;

    this.api.showLoadingOverlay();

    this.blobber.history(key)
      .pipe(first())
      .subscribe({
        next: (content: any) => {

          /* history has been received, push that into a popup dialog for the user to review. */

          this.api.hideOverlay();

          Logger.debug(`[FileBrowserWindow] history ${key} => `, content);

          /*
           * we'll get back an array of objects (metadata for each instance):
           *
           *   oid	37
           *   data	"/var/www/html/blobs/886a4ba2-e513-4bec-b082-5746db88af75/upload:0:level_1_test_5_stop_city.json"
           *   size	109597
           *   key	"/users/mgarvin/level_1_test_5_stop_city.json"
           *   guid	"886a4ba2-e513-4bec-b082-5746db88af75"
           *   name	"level_1_test_5_stop_city.json"
           *   type	"json"
           *   tags	[]
           *   version	""
           *   instance	0
           *   owner	"mgarvin"
           *   blob_sha	"c21f419267485d1cba2a333e3dfd3ec3c3908c71"
           *   created	"2021-12-23 19:53:14"
           *   modified	"2021-12-23 19:53:14"
           *   meta	"/var/www/html/blobs/886a4ba2-e513-4bec-b082-5746db88af75/upload:0:level_1_test_5_stop_city.json.meta.json"
           *
           * The Git style commit is the 'version' field, if its empty the instance is not under version
           * control.
           *
           */

          this.modals.open('blob-history-modal', null, null, {key: key, history: content});

        },
        error: (err): void => {

          this.api.hideOverlay();

          Logger.error(`[FileBrowserWindow] can't find history ${key}: `, errorAsText(err));
        }
      });

  }

  private downloadFile(node: any) {

    if (node.data.type === 'folder') {

      Logger.error(`[FileBrowserWindow] tried to download folder: `, node);

      return  ;
    }

    const  key:   string = node.data.key;

    let   mime: string   = 'application/octet-stream';
    let    txt: boolean  = false;
    let keyBase: string  = key.substring(key.lastIndexOf('/') + 1);

    if (!keyBase || keyBase === '' ) {

      keyBase = key;
    }

    /*
     * everything gets downloaded as binary data, but if it reasonably looks like JSON
     * data, download it as such.
     *
     */

    if (/\.json$/i.test(key)||/\.json$/i.test(keyBase)) {

      mime = 'application/json';
      txt  = true;

    } else if (/\.csv/i.test(key)||/\.csv$/i.test(keyBase)) {

      mime = 'text/csv';
      txt  = true;
    }

    /* fetch the blob */

    this.api.showLoadingOverlay();

    this.blobber.contents(key, 'latest', txt ? 'text' : 'blob')
      .pipe(first())
      .subscribe({
        next: (content: any) => {

          this.api.hideOverlay();

          Logger.debug(`[FileBrowserWindow] downloading ${keyBase} => ${mime}`);

          let blob: Blob;

          if (typeof content === 'string') {

            /* it was presumed to be text */

            blob = new Blob([content], {type: mime});

          } else {

            /* its taken as a binary blob */

            blob = content as Blob;
          }

          const url = window.URL.createObjectURL(blob);

          /* set up a fake one-time link with the right download name and the blob attached as the download */

          const element: HTMLAnchorElement = document.createElement('a');
          element.href = url;
          element.download = keyBase || 'download';

          element.style.display = 'none';

          document.body.appendChild(element);

          /* do it */

          element.click();

          /* clean up */

          document.body.removeChild(element);

        },

        error: (err): void => {

          this.api.hideOverlay();

          Logger.error(`[FileBrowserWindow] can't find content ${key}: `, errorAsText(err));
        }

      });

  }

  /** this version of deleteFilesFolders,  */
  private deleteHistoryFilesFolders(nodes: any) {

    this.deleteFilesFolders(nodes, true);
  }

  /** delete the selected files and folders, except where they are not allowed to */
  private deleteFilesFolders(nodes: any, allHistory: boolean=false) {

    let fbw: FileBrowserWindow = this;

    fbw.api.showLoadingOverlay();

    const userid:        string = fbw.auth.userOfSession.username;
    const isAdmin:      boolean = fbw.auth.userOfSession.isAdmin;
    const todo:           any[] = [];
    const foldersToDo:    any[] = [];

    let total:           number = 0;
    let completed:       number = 0;
    let progress:        number = 0.0;

    Logger.debug(`[FileBrowserWindow] collecting items to delete `, nodes);

    nodes.forEach((x: any) => {

      const key: string = x.key;

      if (isAdmin) {

        /* all your base are belong to us */

        if (x.type === 'folder') {

          foldersToDo.push(x);

          return;

        } else {

          todo.push(x);

          return;
        }
      }

      if (/^\/users\/[^\/]+\/projects/.test(key)) {

        Logger.debug(`[FileBrowserWindow] can not directly delete project files.`);

        return ;
      }

      const match = key.match(/^\/users\/([^\/]+)/);

      if (match) {

        if (match[1] !== userid) {

          Logger.debug(`[FileBrowserWindow] can't delete stuff from other user's folder `, match[1], ' != ', userid, x.key);

          return ;
        }
      }

      if (x.type !== 'folder') {

        if (x.owner !== userid) {

          /* can't delete other peoples stuff */

          Logger.debug(`[FileBrowserWindow] blob not owned by you `, x.owner, ' != ', userid, x.key);

          return;
        }
      }

      if (/^\/users\/[^\/]+\/projects/.test(key)) {

        Logger.debug(`[FileBrowserWindow] can not directly delete project files.`);

        return ;
      }

      /* allow */

      if (x.type === 'folder') {

        foldersToDo.push(x);

      } else {

        todo.push(key);
      }

    });

    total = todo.length + foldersToDo.length;

    if (total === 0) {

      Logger.debug(`[FileBrowserWindow] no deletes possible.`);

      /* nothing to do */

      fbw.api.hideOverlay();

      return ;
    }

    Logger.debug(`[FileBrowserWindow] deleting N=${total} (${allHistory})`);

    /*
     * we use the fork & join pattern to get all the files deleted in parallel
     * and then at the end we refresh our listing.
     *
     * tutorials on using forkJoin:
     *
     * @link https://www.agiratech.com/rxjs-error-handling-with-forkjoin/
     * @link https://medium.com/@swarnakishore/performing-multiple-http-requests-in-angular-4-5-with-forkjoin-74f3ac166d61
     *
     */

    /* create the observables for deleting folders and blobs... */

    const observables: any[] = [];
    const tracker: Map<Observable<Object>, {key: string}> = new Map<Observable<Object>, {key: string}>();
    const taskId: string     = TaskUpdate.generateId();
    const failed: any[]      = [];

    /* add in the direct blobs ... */

    for (const blobItem of todo) {

      /* as we copy we are also forcing the type to be whatever the user selected as the import type */

      let bo = fbw.blobber.remove(blobItem.key, 'latest', allHistory).pipe(

        catchError((err , obs) => {

          let stream: {key: string} = tracker.get(obs);

          Logger.debug(`[FileBrowserWindow] ERROR ${stream.key}`, err);

          let message = "Delete blob Failed!";

          if (isset(err.statusText)) {

            message = err.statusText;
          }

          if (isset(err.error.message)) {

            message = err.error.message;
          }

          let update  = new TaskUpdateFileFolderDeleteFailed(taskId,  message);

          Logger.debug(`[FileBrowserWindow] progress: ${tracker.get(obs)} delete FAILED`, update);

          fbw.taskMonitor.progress(update);

          return of(null);
        })
      );

      observables.push(bo);

      tracker.set(bo, {key: blobItem.key});
    }

    /* add in the folders ... */

    for (const folderItem of foldersToDo) {

      /* the removePrefix() method will do a recursive delete on anything that "begins with" this path */

      let fo = fbw.blobber.removePrefix(folderItem.key).pipe(

        catchError((err , obs) => {

          let stream: {key: string} = tracker.get(obs);

          Logger.debug(`[FileBrowserWindow] ERROR ${stream.key}`, err);

          let message = "Delete folder Failed!";

          if (isset(err.statusText)) {

            message = err.statusText;
          }

          if (isset(err.error.message)) {

            message = err.error.message;
          }

          /* track partial task failure ... */

          failed.push(message);

          Logger.error(`[FileBrowserWindow] delete failed on: `, message);

          return of(null);
        })
      );

      observables.push(fo);

      tracker.set(fo, {key: folderItem.key});
    }

    /* now fork & join to do them all in parallel */

    Logger.debug(`[FileBrowserWindow] direct delete on ${observables.length} files/folders...`);

    forkJoin(
      observables.map(
        o => o.pipe(

          tap((e) => {

            if (!e) {

              return ;
            }

            let stream: {key: string} = tracker.get(o);

            completed++;

            progress = (completed / total) * 100.0;

            Logger.debug('[FileBrowserWindow] . deleted: ', stream.key);

            /* track the progress through the batch delete */

            fbw.taskMonitor.progress(new TaskUpdateFileFolderDelete(taskId, stream.key, completed, total));

          })

      ))).subscribe({
      next: (results) => {

        /* all the files in this selection got deleted ok */

        Logger.debug('[FileBrowserWindow] deleted: ', results);

        fbw.api.hideOverlay();

        /* do any housekeeping after the deletion is all done... */

        Logger.debug(`[FileBrowserWindow] all deletes are complete, REFRESHING...`);

        /* track the task completion / failure */

        if (failed.length > 0) {

          /* at least some of them failed to delete */

          fbw.taskMonitor.progress(new TaskUpdateFileFolderDeleteFailed(taskId, `${failed.length} of ${total} items failed to delete.`));

        } else {

          fbw.taskMonitor.progress(new TaskUpdateFileFolderDeleteComplete(taskId, `Deleted ${total} files/folders.`));
        }

        /* refresh */

        fbw.navigate(fbw.focus);

      },
      error: (err): void => {

        fbw.api.hideOverlay();

        Logger.error('[FileBrowserWindow] problem deleting files/folders: ', errorAsText(err));

      }
    });
  }

  private uploadFiles(): void {

    let fbw: FileBrowserWindow = this;

    Logger.debug(`[FileBrowserWindow] uploadFiles - launch upload...`);

    /* go through a dialog to get blob type and files to upload */

    fbw.api.showLoadingOverlay();

    fbw.modals.open(
      'add-upload-modal',
      (data, resolve, reject): void => {

        const files: File[]  = data.files as File[];
        const type: BlobType = data.type as BlobType;

        Logger.debug('[FileBrowserWindow] uploadFiles - validating (upload): ', files, FileBlob.stringOfType(type));

        /* do we even have files? */

        if (files.length === 0) {

          fbw.api.hideOverlay();

          reject('Must upload at least one file.');

          return ;
        }

        /*
         * we have to go to the backend to check for existence of any blob at this path we're about to try to
         * upload to.  We can't just look in the current folder listing, because we're using infinite scrolling,
         * so we might not have the whole folder loaded in the browser.
         *
         */

        /*
         * first, build an observable for each file to be uploaded, which can hand us back the file exists or
         * not status.
         *
         */

        const observables: Observable<any>[] = [];
        const tracker: Map<Observable<Object>, {key: string, file: File}> = new Map<Observable<Object>, {key: string, file: File}>();
        let errorFiles: Map<File, string>    = new Map<File, string>();

        for (const f of files) {

          let [storage, blobType] = fbw.keyAndType(type, f.name);

          /* bake in support for catching and dealing with errors during the backend call... */

          let o: Observable<Object> = fbw.blobber.fileordir(storage).pipe(
            catchError((err , obs: Observable<Object>) => {

              let errorFile: File | null = tracker.get(obs)?.file ?? null;

              if (errorFile) {

                Logger.error(`[FileBrowserWindow] uploadFiles - problems with fileordir (${errorFile.name}): `, errorAsText(err));

                errorFiles.set(errorFile, errorAsText(err));
              }

              return of(null);
            })
          );

          observables.push(o);

          tracker.set(o, {
            key: storage,
            file: f
          });
        }

        /* now fork & join to do them all in parallel */

        Logger.debug(`[FileBrowserWindow] uploadFiles - checking for ${observables.length} upload file name clashes...`);

        forkJoin(observables).subscribe({
          next: (results: any[]): void => {

            Logger.debug(`[FileBrowserWindow] uploadFiles - existence results: `, results);

            /* if any of the results are "exists already"...then we reject */

            for (let check of results) {

              /*
               * if the file or folder exists, the info object will have a file field that
               * is false for files and true for folders. This is how we know it already exists
               * or not.
               *
               */

              if (check?.file) {

                /* it exists already */

                let name              = check?.name ?? '<unknown>';
                let file: File | null = files.find(x => x.name == name) ?? null;

                if (file) {

                  let msg: string = `${name} already exists in this folder.`;

                  errorFiles.set(file, msg);
                }

                Logger.error(`[FileBrowserWindow] ${name} already exists in this folder (${fbw.focus}).`);

              }

            }

            /* if we had any errors while calling the backend, we reject */

            if (errorFiles.size > 0) {

              let msg: string = 'Upload errors: ';

              for (let [file, err] of errorFiles.entries()) {

                msg += `${file.name}: ${err}, `;
              }

              reject(msg);

              fbw.api.hideOverlay();

              return ;
            }

            /* ok, we are in the clear, resolve as success */

            resolve({files, type});
          },

          error: (err): void => {

            /* problems calling fileordir? reject ... */

            Logger.error(`[FileBrowserWindow] uploadFiles - problem with backend call to fileorexists?`, errorAsText(err));

            reject(errorAsText(err));

            fbw.api.hideOverlay();
          }

        });

      },
      (data: any) => {

        const files:  File[] = data.files as File[];
        const type: BlobType = data.type  as BlobType;

        this.doUpload(type, files);

      }, {
        types: [
          BlobType.File,
          BlobType.Plant,
          BlobType.Traffic,
          BlobType.Scenario,
          BlobType.SimResult,
          BlobType.Project,
          BlobType.KPI,
          BlobType.TPC,
          BlobType.Schedule,
          BlobType.Route,
          BlobType.Forecast
        ]
      });

  }

  /** helper to figure out the proper storage key and typing for a blob */
  private keyAndType(type: BlobType, file: string, typeMap?: Map<string, string>): [storage: string, warpType: BlobType] {

    let fbw: FileBrowserWindow = this;

    /* if the default is File (generic), then try to guess the type... */

    let    warpType: BlobType = FileBlob.guessType(type as BlobType, file, '');
    let      folder: string   = fbw.focus;
    let        base: string   = file.split('.').slice(0, -1).join('.');
    let         ext: string   = file.substring(file.lastIndexOf('.') + 1);

    /*
     * if the parent we are dropping on, has a known extension type (i.e. it's a file), then
     * infer a new folder which is that file path...without the extension.
     *
     */

    if (FileBlob.impliedType(folder) != BlobType.None) {

      /* keep everything up to (but not including) the trailing (recognized) extension */

      folder = folder.substring(0, folder.length - ext.length - 1);
    }

    if (warpType == BlobType.None || warpType == BlobType.File || warpType == BlobType.Folder) {

      if (FileBlob.guessType(BlobType.None, file, '') == BlobType.None) {

        ext      = file;
        base     = file;
        warpType = BlobType.NoExtension;
      }

    } else {

      /* if is clearly typed, then ensure the extension reflects the type */

      ext = FileBlob.extensionOfType(warpType);
    }

    /* make sure there actually is an extension... */

    if (ext === file) {

      ext = '';

    } else {

      /*
       * if they asked for a generic type None | File, and we have a more specific type now...because they
       * gave us a file extension, we don't want to auto-assign a file extension, we want to use it, and the
       * the more precise file type.
       *
       */

      if ((type == BlobType.File) || (type == BlobType.Folder) || (type == BlobType.None)) {

        if ((warpType != BlobType.File) && (warpType != BlobType.Folder) && (warpType != BlobType.None)) {

          type = warpType;
        }
      }

    }

    /*
     * finally, if they gave us a type map, it means they have some type overrides we should
     * be using.
     *
     */

    let blobType: BlobType = warpType;

    if (typeMap) {

      if (typeMap.has(file)) {

        blobType = FileBlob.typeofString(typeMap.get(file));

      }
    }

    /* build the key to use for the blob... */

    if (warpType == BlobType.File || warpType == BlobType.Folder || type == BlobType.File || type == BlobType.Folder) {

      /*
       * if they are using generic "file" type...don't don't type it, don't give it an
       * extension.  let them have a naked filename (and they can add an extension if they
       * want to).
       *
       */

      warpType = BlobType.NoExtension;
      type     = blobType;
    }

    const   storage: string   = folder + '/' +
      (base !== '' ? base : file) +
      ((warpType !== type) ? FileBlob.extensionOfType(warpType) : ext);

    Logger.debug(`[FileBrowserWindow] keyAndType - [${file}] => ${storage} : ${FileBlob.stringOfType(warpType)} => : ${FileBlob.stringOfType(blobType)}`);

    return [storage, blobType];
  }

  private doUpload(type: BlobType, files: File[], typeMap?: Map<string, string>) {

    Logger.debug('[FileBrowserWindow] UPLOADING: ', files, FileBlob.stringOfType(type), typeMap);

    let fbw: FileBrowserWindow = this;

    /* pre-flight check, no file can be bigger than our backend file upload max size (currently 512MB) */

    let tooBig: File = null;

    files.forEach((f: File) => {

      if (tooBig) {

        return ;
      }

      if (f.size >= (1000 * 1000 * 512)) {

        tooBig = f;
      }
    });

    if (tooBig) {

      fbw.modals.open('error-msg-modal',
        () => {
        },
        () => {
        },
        {
          message: `${tooBig.name} is too big (${bytesToSize(tooBig.size)} > 512Mb) to upload.`
        });

      /* can not continue */

      return ;
    }

    /*
     * for each file we create a blob of the given type, except for File; it's not given
     * an explicit type, we will try to guess it from the file extension and if we
     * can't, then we fall back to File.
     *
     */

    /*
     * we use the fork & join pattern to get all the files created as blobs in parallel
     * and then at the end, we actually add them to the project as blob elements.
     *
     * tutorials on using forkJoin:
     *
     * @link https://www.agiratech.com/rxjs-error-handling-with-forkjoin/
     * @link https://medium.com/@swarnakishore/performing-multiple-http-requests-in-angular-4-5-with-forkjoin-74f3ac166d61
     *
     */

    Logger.debug('[FileBrowserWindow] creating blobs...');

    /* create the observables for creating blobs... */

    const observables: any[] = [];
    const tracker: Map<Observable<Object>, {key: string, id: string, total: number, type: BlobType, icon: string}> =
      new Map<Observable<Object>, {key: string, id: string, total: number, type: BlobType, icon: string}>();

    for (const f of files) {

      let [storage, blobType] = fbw.keyAndType(type, f.name, typeMap);

      /*
       * on doing Angular requests with progress:
       *
       *   https://nils-mehlhorn.de/posts/angular-file-download-progress
       *   https://angular.io/api/common/http/HttpProgressEvent
       */

      Logger.debug('[FileBrowserWindow] new blob: ',  storage, blobType);

      let o: Observable<any> = fbw.blobber.create(
        storage,
        blobType,
        f,
        f.name,
        'uploaded blob',
        false,
        true
      ).pipe(
        catchError((err , obs) => {

          let stream: {key: string, id: string, total: number, type: BlobType, icon: string} = tracker.get(obs);

          Logger.debug(`[FileBrowserWindow] ERROR ${stream.key}`, err);

          let message = "Upload Failed!";

          if (isset(err.statusText)) {

            message = err.statusText;
          }

          if (isset(err.error.message)) {

            message = err.error.message;
          }

          let update  = new TaskUpdateFileUploadFailed(stream.id, stream.key, stream.icon, message);

          Logger.debug(`[FileBrowserWindow] progress: ${tracker.get(o)} uploaded FAILED`, update);

          fbw.taskMonitor.progress(update);

          return of(null);
        })
      );

      observables.push(o);

      tracker.set(o, {
        key: storage,
        id: TaskUpdate.generateId(),
        total: f.size,
        type: blobType,
        icon: FileBlob.iconOfType(blobType)
      });
    }

    /* now fork & join to do them all in parallel */

    forkJoin(observables.map(
      o => o.pipe(

        tap((e: HttpEvent<any>) => {

          if (!e) {

            return ;
          }

          if (e.type == HttpEventType.UploadProgress) {

            /* upload progress */

            let stream: {key: string, id: string, total: number, type: BlobType, icon: string} = tracker.get(o);

            let update = new TaskUpdateFileUpload(stream.id, stream.key, stream.icon, e.loaded, e.total);

            Logger.debug(`[FileBrowserWindow] progress: ${stream.key} `, update);

            fbw.taskMonitor.progress(update);

          } else if (e.type == HttpEventType.Sent) {

            /* the request was sent */

          }  else if (e.type == HttpEventType.ResponseHeader) {

            /* we got a response back, this means the upload was completed. */

            let stream: {key: string, id: string, total: number, type: BlobType, icon: string} = tracker.get(o);

            let update = new TaskUpdateFileUploadComplete(stream.id, stream.key, stream.icon, stream.total);

            Logger.debug(`[FileBrowserWindow] progress: ${tracker.get(o)} uploaded COMPLETE`, update);

            fbw.taskMonitor.progress(update);

          } else if (e.type == HttpEventType.Response) {

            Logger.debug(`[FileBrowserWindow] progress: ${tracker.get(o)} uploaded RESPONSE`, e);

          }

        })

      ))).subscribe({
      next: (results) => {

        /* everything got created ok */

        this.api.hideOverlay();

        Logger.debug('[FileBrowserWindow] blobs updated: ', results);

        /* refresh */

        this.navigate(this.focus);

      },

      error: (err): void => {

        this.api.hideOverlay();

        if (err) {

          Logger.error('[FileBrowserWindow] problems creating blobs: ', errorAsText(err));
        }

      }

    });
  }

  /** create a new empty file in the current folder */
  private newEmptyFile(): void {

    let fbw: FileBrowserWindow = this;

    fbw.api.showLoadingOverlay();

    fbw.modals.open(
      'new-empty-modal',
      (data, resolve, reject): void => {

        const name: string   = data.name as string;
        const type: BlobType = data.type as BlobType;

        Logger.debug('[FileBrowserWindow] newEmptyFile - validating: ', name, FileBlob.stringOfType(type));

        /* is it a valid name? */

        if (name.trim() === '') {

          fbw.api.hideOverlay();

          reject('File name can not be empty.');

          return ;
        }

        /* do we already have a file of that name? */

        let [storage, blobType] = fbw.keyAndType(type, name);

        this.blobber.fileordir(storage)
          .pipe(first())
          .subscribe({
            next: (check: any): void => {

              Logger.debug(`[FileBrowserWindow] newEmptyFile - exists result: `, check);

              /* does it exist already? */

              if (check?.file) {

                this.api.hideOverlay();

                Logger.error(`[FileBrowserWindow] newEmptyFile - file (${check.name}) already exists in folder (${fbw.focus})`);

                this.api.hideOverlay();

                reject(`${check.name} already exists in this folder`);

                return ;
              }

              /* doesn't exist yet, accept it ... */

              Logger.debug(`[FileBrowserWindow] newEmptyFile - accepting!`);

              resolve({name, type});

            },
            error: (err: any): void => {

              this.api.hideOverlay();

              Logger.error(`[FileBrowserWindow] newEmptyFile - problems calling fileordir? `, errorAsText(err));

              reject(errorAsText(err));
            }

          });

      },
      (data: any): void => {

        const name: string   = data.name as string;
        let   type: BlobType = data.type as BlobType;

        if (type == BlobType.File) {

          /* if they are using the generic File type, then leave it un-typed, a naked file name */

          type = BlobType.NoExtension;
        }

        /*
         * the blob is stored in the project folder, and from there we make it relative
         * to this node in the project
         *
         */

        const      blobData: string = '{json: "empty"}';
        const        folder: string = this.focus.replace(/\.[^/\\.]+$/, '');
        const       storage: string = folder + '/' + name +  FileBlob.extensionOfType(type);

        Logger.debug('[FileBrowserWindow] new (empty) blob: ',  storage, type);

        this.blobber.create(storage, type, blobData, name, 'empty file')
          .pipe(first())
          .subscribe({
            next: (blob): void => {

              this.api.hideOverlay();

              Logger.debug('[FileBrowserWindow] new (empty) blob saved: ', blob);

              this.navigate(this.focus);

            },
            error: (err) => {

              this.api.hideOverlay();

              Logger.error('[FileBrowserWindow] could not create empty blob: ', errorAsText(err));
            }

          });

      }, {
        placeholder: 'Empty Blob Name',
        types: [
          BlobType.File,
          BlobType.Plant,
          BlobType.Traffic,
          BlobType.Scenario,
          BlobType.SimResult,
          BlobType.Project,
          BlobType.KPI,
          BlobType.TPC,
          BlobType.Schedule,
          BlobType.Route,
          BlobType.Forecast
        ]
      });

  }

  /** create a new folder in the current folder */
  private newFolder(): void {

    let fbw: FileBrowserWindow = this;

    this.api.showLoadingOverlay();

    this.modals.open(
      'new-folder-modal',
      (data, resolve, reject): void => {

        const name: string   = data as string;

        Logger.debug('[FileBrowserWindow] newFolder - validating: ', name);

        /* is it a valid name? */

        if (name.trim() === '') {

          this.api.hideOverlay();

          reject('Folder name can not be empty.');

          return ;
        }

        /* do we already have a file of that name? */

        let [storage, blobType] = fbw.keyAndType(BlobType.Folder, name);

        this.blobber.fileordir(storage)
          .pipe(first())
          .subscribe({
            next: (check: any): void => {

              Logger.debug(`[FileBrowserWindow] newFolder - exists result: `, check);

              /* does it exist already? */

              if ((check?.file ?? null) !== null) {

                this.api.hideOverlay();

                Logger.error(`[FileBrowserWindow] newFolder - folder (${check.name}) already exists in folder (${fbw.focus})`);

                this.api.hideOverlay();

                reject(`${check.name} already exists in this folder`);

                return ;
              }

              /* doesn't exist yet, accept it ... */

              Logger.debug(`[FileBrowserWindow] newFolder - accepting!`);

              resolve({name, type: BlobType.Folder});

            },
            error: (err: any): void => {

              this.api.hideOverlay();

              Logger.error(`[FileBrowserWindow] newFolder - problems calling fileordir? `, errorAsText(err));

              reject(errorAsText(err));
            }

          });

      },
      (data: any): void => {

        const name: string = data?.name ? data.name : (data as string);

        /*
         * in blob land, there is no such thing as a directory, but we want user's to be able
         * to logically organize things, using a file browser metaphor, It's what they are used
         * to.  So...we create a bogus "hidden" file inside the directory...which again...doesn't
         * actually exist. :)  Our file browser will then by default not show hidden files...so
         * the directory will show up when browsing, but be empty until they add legit stuff to
         * it.
         *
         * If our folder marker gets deleted...s'ok, they can make the directory again.  If other
         * files are there...the directory remains anyway, until all files are removed.
         *
         * Folders can be deleted manually...when the only child item is the directory marker.
         */

        const      blobData: string = `{hidden: true, parent: "${this.focus}", marker: "${name}"}`;
        const        folder: string = this.focus.replace(/\.[^/\\.]+$/, '');
        let         storage: string = folder + '/' + name +  '/.folder-marker.json';

        /* if they gave us an absolute path...then we don't append as a sub-folder */

        if (name.charAt(0) === '/') {

          storage = name +  '/.folder-marker.json';
        }

        /* make double sure there are no extra delimiters */

        storage = storage.replace(/^\/+/, '/');

        Logger.debug('[FileBrowserWindow] new folder marker: ',  storage, BlobType.File);

        this.blobber.create(storage, BlobType.File, blobData, '.folder-marker.json', 'folder marker')
          .pipe(first())
          .subscribe({
            next: (blob): void => {

              this.api.hideOverlay();

              Logger.debug('[FileBrowserWindow] new (empty) blob saved: ', blob);

              this.navigate(this.focus);

            },

            error: (err): void => {

              this.api.hideOverlay();

              Logger.debug('[FileBrowserWindow] can not create folder: ', errorAsText(err));
            }

          });

      });

  }

  /** update the focus to current */
  private updateFocus(path: string): void {

    $(`#blob-path-input-${this.meta.id}`).val(path);
    this.focus = path;
    this.title = path;
  }

  private error(msg: string) {

    const display = $(`#${this.meta.id} .gui-error-line`);

    $(display).html(msg);
  }

  /** helper to make sure the navigation buttons are in the right state */
  public updateNavButtons() {

    /* update the navigation buttons */

    const    back: JQuery = $(`#browse-blob-back-click-${this.meta.id}`);
    const forward: JQuery = $(`#browse-blob-forward-click-${this.meta.id}`);

    $(back).removeClass('disabled');
    $(forward).removeClass('disabled');

    if (this.undoManager.undoSize() === 0) {

      $(back).addClass('disabled');
    }

    if (this.undoManager.redoSize() === 0) {

      $(forward).addClass('disabled');
    }
  }

  public goFolder(folder: string): void {

    Logger.debug('[FileBrowserWindow] goFolder - jumping to: ', folder);

    /* close the drop-down */

    this.toggleQuintiqDropdown();

    /* jump to that directory */

    this.navigate(folder);
  }

  public toggleQuintiqDropdown(): void {

    const    panel: JQuery = $(`#browse-blob-quintiq-drop-panel-${this.meta.id}`);

    if ($(panel).hasClass('open')) {

      Logger.debug('[FileBrowserWindow] toggleQuintiqDropdown: CLOSED');

      $(panel).removeClass('open');

    } else {

      Logger.debug('[FileBrowserWindow] toggleQuintiqDropdown: OPEN');

      $(panel).addClass('open');

    }

  }

  /** wrapper for 'ls' that tracks it with history, so we can do back/forward navigation */
  public navigate(path: string): void {

    /* do we actually have new history? */

    if ((path === this.focus) && (this.undoManager.undoSize() === 0)) {

      /* it's the same thing, just refresh */

      this.ls(path);

      return ;
    }

    /* track the navigation history... */

    Logger.debug('[FileBrowserWindow] navigating to: ', path);

    const cmd: ICommand        = new FBNavCommand(this.focus, path, this);

    /* use the command to do the navigation */

    cmd.execute();

    this.undoManager.insert(cmd);
  }

  /** fetch contents of folder and display */
  public ls(path: string): void {

    this.error('');

    let fbw: FileBrowserWindow = this;

    fbw.api.showLoadingOverlay();

    fbw.blobber.listWithTotal(fbw.showHidden, path, '/', 0, 100)
      .pipe(first())
      .subscribe({
        next: (listing: any): void => {

          fbw.updateNavButtons();

          /* ok, show the files in this folder! */

          fbw.updateFocus(path);

          let total: number  = listing?.result?.total ?? 0;
          let hidden_included: number = listing?.result?.hidden_included ?? 0;
          let results: any[] = [];

          if (Array.isArray(listing?.result?.results)) {

            results = listing.result.results
          }

          Logger.debug(`[FileBrowserWindow] navigated: ${path}, total=${total}, [${results.length}] rows=`, results);

          /* if we didn't get anything, there is nothing to show ... */

          fbw.api.applyColumnState({
            state: [{colId: 'name', sort: 'asc'}],
            defaultState: {sort: null}
          });

          this.api.purgeInfiniteCache();

          fbw.api.setGridOption('datasource', null);

          if (results.length === 0) {

            /*
             * we make folders either by implication (more than one file under a prefix), or by
             * forcing logical folders with hidden files.  So if they navigate to a path, its
             * only truly non-existent, if there are no descendants there, INCLUDING hidden files.
             *
             */

            if (hidden_included == 0) {

              fbw.error('Folder does not exist.');

              fbw.api.sizeColumnsToFit();

              fbw.api.hideOverlay();

              return;
            }
          }

          /*
           * create a new datasource to virtual scroll this folder, AGGrid will call the IDatasource
           * as needed to redraw the grid rows.
           *
           */

          let datasource: FileBrowserDatasource =
            new FileBrowserDatasource(fbw.blobber, path, fbw.showHidden, total, results);

          fbw.api.setGridOption('datasource', datasource);

          fbw.api.sizeColumnsToFit();

          if (fbw.highlight.trim().length != 0) {

            /* if there is a highlight...look for a row with that file name and select it. */

            /*
             * HACK ALERT: this isn't a great method; we're just hoping that datasource set
             * the first page of results before out timeout fires, and we're further hoping
             * that the highlighted row is in the first page of data.  When using 'infinite'
             * scroll, the forEacNode() method ONLY applies to whatever has been cached so
             * far.
             *
             */

            setTimeout((): void => {

              Logger.debug('[FileBrowserWindow] looking for highlight row: ', this.highlight);

              fbw.api.forEachNode((node: IRowNode): void => {

                if (!node?.data) {

                  return ;
                }

                /* find the matching row */

                if (node.data.name === fbw.highlight) {

                  /* select */

                  Logger.debug(`[FileBrowserWindow] . selecting highlighted, ${node.id}: `, node.data);

                  node.setSelected(true);

                }

              });

              fbw.highlight = '';

            }, 500);

          }

          fbw.api.hideOverlay();
        },

        error: (err): void => {

          fbw.updateNavButtons();

          fbw.api.hideOverlay();

          Logger.error('[FileBrowserWindow] could not list folder: ', errorAsText(err));

          fbw.error('Folder does not exist.');
        }

      });


  }
}
